import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast';
import Breadcrumb from '../../components/common/Breadcrumb'
import ReactDataTable from 'react-data-table-component';
import { IconBan, IconCheck, IconEye } from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import { useHrPendingTimesheets, useHrApproveTimesheet, useRejectTimesheet } from '../../hooks/useTimesheets';
import { useEmployees } from '../../hooks/useEmployees';

const AVATAR_COLORS = ['#4f46e5', '#0891b2', '#059669', '#d97706', '#dc2626', '#7c3aed', '#db2777'];

export default function HrTimesheetApprovals() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const orgId = user?.orgId;

    const { data: pendingTimesheets, isLoading } = useHrPendingTimesheets(orgId);
    const { data: employees } = useEmployees(orgId);

    const approveTimesheet = useHrApproveTimesheet();
    const rejectTimesheet = useRejectTimesheet();

    const timesheets = pendingTimesheets?.timesheets || pendingTimesheets?.results || (Array.isArray(pendingTimesheets) ? pendingTimesheets : []);

    const [actionModal, setActionModal] = useState({ isOpen: false, timesheet: null });
    const [rejectReason, setRejectReason] = useState('');
    const [filterText, setFilterText] = useState('');
    const [resetPaginationToggle] = useState(false);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const getMonthName = (month) => {
        const months = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        return months[month - 1] || '';
    };

    const getStatusBadge = (status) => {
        const s = status?.toLowerCase();
        const map = {
            manager_approved: <span className='badge badge-warning'>Pending HR Review</span>,
            approved: <span className='badge badge-success'>Approved</span>,
            rejected: <span className='badge badge-danger'>Rejected</span>,
        };
        return map[s] || <span className='badge badge-secondary'>{status}</span>;
    };

    const handleApprove = (timesheet) => {
        approveTimesheet.mutate({ id: timesheet.id, orgId }, {
            onSuccess: () => toast.success('Timesheet approved by HR successfully'),
            onError: (err) => toast.error(`Error: ${err.message}`)
        });
    };

    const handleReject = () => {
        if (!rejectReason.trim()) {
            toast.error('Please provide a reason for rejection');
            return;
        }
        rejectTimesheet.mutate({ id: actionModal.timesheet.id, reason: rejectReason, orgId }, {
            onSuccess: () => {
                setActionModal({ isOpen: false, timesheet: null });
                setRejectReason('');
                toast.success('Timesheet rejected successfully');
            },
            onError: (err) => toast.error(`Error: ${err.message}`)
        });
    };

    const columns = [
        {
            name: 'Employee',
            selector: row => row.employeeName,
            sortable: true,
            cell: row => {
                const initials = row.employeeName
                    ? row.employeeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                    : '?';
                const bg = AVATAR_COLORS[(row.employeeName?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
                return (
                    <div className='flex items-center gap-10'>
                        <div style={{ backgroundColor: bg }} className='w-[35px] h-[35px] rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0'>
                            {initials}
                        </div>
                        <div>
                            <p className='font-medium text-sm'>{row.employeeName}</p>
                            <p className='text-xs text-text-secondary'>{row.employeeEmail}</p>
                        </div>
                    </div>
                );
            },
            width: "250px"
        },
        {
            name: 'Period',
            selector: row => `${getMonthName(row.month)} ${row.year}`,
            sortable: true,
            width: "150px"
        },
        {
            name: 'Total Hours',
            selector: row => row.total_hours || 0,
            sortable: true,
            width: "120px",
            center: true,
            cell: row => <span className="font-semibold">{row.total_hours || 0}h</span>
        },
        {
            name: 'Mgr Approved',
            selector: row => formatDate(row.manager_approved_at),
            sortable: true,
            width: "150px"
        },
        {
            name: 'Status',
            selector: row => row.status,
            format: row => getStatusBadge(row.status),
            sortable: true,
            width: "170px"
        },
        {
            name: 'Actions',
            cell: row => (
                <div className='flex items-center gap-5'>
                    <button
                        onClick={() => navigate(`/approvals/timesheet/hr/${row.id}`)}
                        className='btn-icon text-blue'
                        title="View Timesheet"
                    >
                        <IconEye size={18} />
                    </button>
                    <button
                        onClick={() => handleApprove(row)}
                        className='btn-icon text-success'
                        title="HR Approve"
                    >
                        <IconCheck size={18} />
                    </button>
                    <button
                        onClick={() => { setActionModal({ isOpen: true, timesheet: row }); setRejectReason(''); }}
                        className='btn-icon text-danger'
                        title="Reject"
                    >
                        <IconBan size={18} />
                    </button>
                </div>
            ),
            width: "150px",
            center: true
        }
    ];

    const formattedTimesheets = useMemo(() => {
        return timesheets.map(ts => {
            let employeeObj = null;
            if (employees && employees.length > 0) {
                const empId = typeof ts.employee === 'object' ? ts.employee.id : ts.employee;
                employeeObj = employees.find(e => e.id === empId);
            }
            const employeeName = employeeObj
                ? (employeeObj.name || `${employeeObj.first_name} ${employeeObj.last_name}`)
                : (ts.employee_name || 'Unknown');
            const employeeEmail = employeeObj?.email || employeeObj?.work_email || '';
            return { ...ts, employeeName, employeeEmail };
        });
    }, [timesheets, employees]);

    const filteredItems = formattedTimesheets.filter(item => {
        if (!filterText) return true;
        const search = filterText.toLowerCase();
        const s = (v) => String(v || '').toLowerCase();
        return s(item.employeeName).includes(search) || s(item.status).includes(search) || s(getMonthName(item.month)).includes(search);
    });

    const subHeaderComponent = useMemo(() => (
        <div className='flex gap-5 mb-10 w-full justify-end'>
            <input
                type='text'
                placeholder='Search...'
                className='form-control w-full max-w-[250px]'
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
            />
        </div>
    ), [filterText]);

    if (isLoading) return <div className='p-20 text-center'>Loading...</div>;

    return (
        <div className='p-20 bg-bg-light min-h-screen'>
            <Breadcrumb title="HR Timesheet Approvals" path="Approvals / HR Timesheets" />

            <div className='grid grid-cols-1 md:grid-cols-3 gap-20 mb-30'>
                <div className='bg-white p-20 rounded shadow-sm border border-border'>
                    <p className='text-xs text-text-secondary uppercase font-bold mb-5'>Pending HR Review</p>
                    <h3 className='text-3xl font-bold text-warning'>{filteredItems.length}</h3>
                    <p className='text-sm text-text-secondary mt-5'>Timesheets awaiting HR approval</p>
                </div>
            </div>

            <div className='bg-white rounded shadow-sm p-20'>
                <div className='flex justify-between items-center mb-20'>
                    <h2 className='text-lg font-bold'>Timesheets Pending HR Approval</h2>
                </div>

                {filteredItems.length === 0 ? (
                    <div className='text-center py-40 text-text-secondary'>
                        <p className='text-lg'>No pending timesheets</p>
                        <p className='text-sm mt-5'>No timesheets are awaiting HR approval.</p>
                    </div>
                ) : (
                    <ReactDataTable
                        columns={columns}
                        data={filteredItems}
                        pagination
                        paginationResetDefaultPage={resetPaginationToggle}
                        subHeader
                        subHeaderComponent={subHeaderComponent}
                        highlightOnHover
                        persistTableHead
                    />
                )}
            </div>

            {actionModal.isOpen && (
                <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm'>
                    <div className='bg-white rounded-lg shadow-lg w-full max-w-md p-20'>
                        <h3 className='text-lg font-bold mb-10'>Reject Timesheet</h3>
                        <p className='text-sm text-gray-600 mb-20'>
                            Please provide a reason for rejecting
                            <span className='font-bold text-gray-800'> {actionModal.timesheet?.employeeName}'s</span> timesheet
                            for <span className='font-bold'>{getMonthName(actionModal.timesheet?.month)} {actionModal.timesheet?.year}</span>.
                        </p>
                        <textarea
                            className='w-full border border-gray-300 rounded p-10 text-sm mb-20 focus:border-org-blue-primary outline-none'
                            rows={3}
                            placeholder="Reason for rejection..."
                            value={rejectReason}
                            onChange={e => setRejectReason(e.target.value)}
                        />
                        <div className='flex justify-end gap-10'>
                            <button
                                onClick={() => setActionModal({ isOpen: false, timesheet: null })}
                                className='btn btn-secondary'
                            >
                                Cancel
                            </button>
                            <button onClick={handleReject} className='btn btn-danger'>
                                Reject
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
